import React, { useState, useEffect, useRef } from 'react';
import { 
  Modal, 
  Button, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Divider, 
  CircularProgress,
  Fade,
  Grow,
  LinearProgress,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getQualifierReport } from '../services/api';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { usePDF } from 'react-to-pdf';

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ReportContainer = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 700,
  maxHeight: '90vh',
  overflow: 'auto',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
}));

const ReportCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  backgroundColor: '#FFF5F5',
  border: '1px solid #FF4D4F',
  boxShadow: '0 4px 15px rgba(255, 77, 79, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '150px',
    height: '150px',
    background: 'radial-gradient(circle, rgba(255,77,79,0.1) 0%, rgba(255,255,255,0) 70%)',
    borderRadius: '0 0 0 100%',
  }
}));

const Logo = styled('div')(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  '& img': {
    height: 40,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#FF4D4F',
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    backgroundColor: '#FF4D4F',
  }
}));

const UserProfile = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(255, 77, 79, 0.05)',
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#fff',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  }
}));

const InfoRow = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const ScoreValue = styled(Typography)(({ theme, color }) => ({
  color: color || '#FF4D4F',
  fontWeight: 'bold',
  fontSize: '1.5rem',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '40px',
    height: '2px',
    backgroundColor: '#FF4D4F',
  }
}));

const ProgressBar = styled(LinearProgress)(({ theme, value }) => ({
  height: 10,
  borderRadius: 5,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: '#f0f0f0',
  '& .MuiLinearProgress-bar': {
    backgroundColor: getProgressColor(value),
  }
}));

// Function to determine color based on score percentage
const getProgressColor = (percent) => {
  if (percent >= 80) return '#4caf50'; // Green
  if (percent >= 60) return '#ff9800'; // Orange
  return '#f44336'; // Red
};

// Function to convert percentage to percentile based on ranges with random decimal points
const getPercentileFromPercent = (percent) => {
  // Generate a random decimal between 0 and 0.99
  const randomDecimal = Math.random().toFixed(2).substring(1);
  
  if (percent < 20) return (80);
  if (percent >= 20 && percent < 40) return (85 + parseFloat(randomDecimal)).toFixed(2);
  if (percent >= 40 && percent < 60) return (90 + parseFloat(randomDecimal)).toFixed(2);
  if (percent >= 60 && percent < 80) return (95 + parseFloat(randomDecimal)).toFixed(2);
  return (99 + parseFloat(randomDecimal)).toFixed(2); // Covers both 80-90 and >=90, max is 99.99
};

const SectionCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  }
}));

const IconWithText = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  '& svg': {
    marginRight: theme.spacing(1),
    color: '#FF4D4F',
  }
}));

const DownloadButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const QualifierReport = ({ open, onClose, bookingId }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  const reportRef = useRef(null);
  const { toPDF, targetRef } = usePDF({
    filename: report ? `Qualifier_Report_${report.user_info.name.replace(/\s+/g, '_')}.pdf` : 'report.pdf',
    page: { 
      margin: 10,
      format: 'a4',
      orientation: 'portrait'
    }
  });

  useEffect(() => {
    if (open && bookingId) {
      console.log('QualifierReport: open =', open, 'bookingId =', bookingId);
      fetchReport();
    }
  }, [open, bookingId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      console.log('QualifierReport: Fetching report for bookingId =', bookingId);
      const data = await getQualifierReport(bookingId);
      console.log('QualifierReport: Received data =', data);
      setReport(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load the qualifier report. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloadingPdf(true);
      await toPDF();
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSectionClick = (sectionIndex) => {
    setActiveSection(activeSection === sectionIndex ? null : sectionIndex);
  };

  return (
    <StyledModal 
      open={open} 
      onClose={onClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <ReportContainer>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">Qualifier Test Report</Typography>
            <Button onClick={onClose} color="inherit" sx={{ minWidth: 'auto' }}>&times;</Button>
          </Box>
          
          {loading || downloadingPdf ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="300px">
              <CircularProgress color="error" />
              <Typography variant="body1" mt={2}>
                {downloadingPdf ? 'Generating PDF...' : 'Loading your report...'}
              </Typography>
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
              <Typography color="error">{error}</Typography>
            </Box>
          ) : report ? (
            <>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <DownloadButton 
                  variant="contained" 
                  color="error" 
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  size="small"
                  disabled={downloadingPdf}
                >
                  Download PDF
                </DownloadButton>
              </Box>
              
              <Grow in={true} timeout={500}>
                <ReportCard id="qualifier-report-card" ref={targetRef}>
                  <Logo>
                    <Typography variant="h5" fontWeight="bold">Futurense</Typography>
                  </Logo>
                  
                  <Title variant="h5">Course Assessment Report Card</Title>
                  
                  <UserProfile>
                    <Box width="100%">
                      <Typography variant="body2" color="textSecondary">Name</Typography>
                      <Typography variant="h6">{report.user_info.name}</Typography>
                    </Box>
                  </UserProfile>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <IconWithText>
                        <SchoolIcon />
                        <Box>
                          <Typography variant="body2" color="textSecondary">Course Name</Typography>
                          <Typography variant="body1" fontWeight="medium">{report.course_info.name}</Typography>
                        </Box>
                      </IconWithText>
                    </Grid>
                  </Grid>
                  
                  <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} sm={6}>
                      <IconWithText>
                        <TimerIcon />
                        <Box>
                          <Typography variant="body2" color="textSecondary">Duration</Typography>
                          <Typography variant="body1" fontWeight="medium">{report.course_info.duration}</Typography>
                        </Box>
                      </IconWithText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <IconWithText>
                        <CalendarTodayIcon />
                        <Box>
                          <Typography variant="body2" color="textSecondary">Date of Attempt</Typography>
                          <Typography variant="body1" fontWeight="medium">{report.course_info.date_of_attempt || 'Not available'}</Typography>
                        </Box>
                      </IconWithText>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 4 }} />
                  
                  <SectionTitle variant="h6">
                    <IconWithText sx={{ justifyContent: 'center' }}>
                      <EmojiEventsIcon />
                      <Typography variant="h6" component="span">Overall Performance</Typography>
                    </IconWithText>
                  </SectionTitle>
                  
                  <InfoBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Score</Typography>
                        <ScoreValue variant="body1">
                          {report.performance.total_score} 
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            (out of {report.performance.max_score})
                          </Typography>
                        </ScoreValue>
                        <ProgressBar 
                          variant="determinate" 
                          value={(report.performance.total_score / report.performance.max_score) * 100} 
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Percentile</Typography>
                        <ScoreValue variant="body1">{getPercentileFromPercent(report.performance.overall_percent)}<small>th</small></ScoreValue>
                        <ProgressBar 
                          variant="determinate" 
                          value={report.performance.overall_percent} 
                        />
                      </Grid>
                    </Grid>
                  </InfoBox>
                  
                  <SectionTitle variant="h6">Section-wise Performance</SectionTitle>
                  
                  <InfoBox>
                    {report.section_performance.map((section, index) => (
                      <Grow in={true} timeout={500 + (index * 100)} key={index}>
                        <SectionCard 
                          onClick={() => handleSectionClick(index)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {section.section}
                                </Typography>
                                <Chip 
                                  label={`${section.section_overall_percent}%`} 
                                  size="small" 
                                  color={section.section_overall_percent >= 70 ? "success" : section.section_overall_percent >= 40 ? "warning" : "error"}
                                />
                              </Box>
                              <ProgressBar 
                                variant="determinate" 
                                value={section.section_overall_percent} 
                              />
                              <Box display="flex" justifyContent="space-between" mt={1}>
                                <Typography variant="body2">
                                  Score: {section.section_score_achieved} / {section.section_max_score}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </SectionCard>
                      </Grow>
                    ))}
                  </InfoBox>
                  
                  <Box mt={4} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      This report is generated based on your performance in the qualifier test.
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="success.main">
                        Test completed successfully
                      </Typography>
                    </Box>
                  </Box>
                </ReportCard>
              </Grow>
            </>
          ) : null}
        </ReportContainer>
      </Fade>
    </StyledModal>
  );
};

export default QualifierReport;
